import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { RejectedReason } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { toDecimal } from '~utils/decimal';
import { formatRateWithType } from '~utils/rateFormatting';

import { CHARGES_CELL_SX, NO_VALUE_PLACEHOLDER } from '../chargesGrid';
import RejectedChip from './RejectedChip';

interface ChargeAmountCellProps {
  addOnsAmount?: number;
  amount: number | null;
  isMaterialRateItemized?: boolean;
  isRejected?: boolean;
  materialRate?: number | null;
  materialRateType?: string | null;
  rate?: number | null;
  rateType?: string | null;
  rejectedReason?: RejectedReason | null;
  sx?: SxProps<Theme>;
  taxAmount?: number;
  wasRate?: number | null;
}

// An order that doesn't itemize its material bills freight and material as one rate, so
// the two are added up (shown as F+M) when they're charged by the same unit
function getDisplayedRates({
  isMaterialRateItemized,
  materialRate,
  materialRateType,
  rate,
  rateType,
}: Pick<
  ChargeAmountCellProps,
  'isMaterialRateItemized' | 'materialRate' | 'materialRateType' | 'rate' | 'rateType'
>) {
  const hasMaterialRate = materialRate !== null && materialRate !== undefined;
  const canCombineRates =
    !isMaterialRateItemized &&
    hasMaterialRate &&
    rate !== null &&
    rate !== undefined &&
    materialRateType === rateType;

  if (canCombineRates) {
    return {
      isCombined: true,
      rate: toDecimal(rate).plus(toDecimal(materialRate)).toNumber(),
      separateMaterialRate: null,
    };
  }

  return {
    isCombined: false,
    rate: rate ?? null,
    separateMaterialRate: hasMaterialRate ? materialRate : null,
  };
}

export default function ChargeAmountCell({
  addOnsAmount = 0,
  amount,
  isMaterialRateItemized = true,
  isRejected = false,
  materialRate = null,
  materialRateType = null,
  rate = null,
  rateType = null,
  rejectedReason = null,
  sx,
  taxAmount = 0,
  wasRate = null,
}: ChargeAmountCellProps) {
  const { currencyFormatter } = useCompanyCurrency();
  const displayedRates = getDisplayedRates({
    isMaterialRateItemized,
    materialRate,
    materialRateType,
    rate,
    rateType,
  });
  const formatShortRate = (value: number, type: string | null) =>
    formatRateWithType(value, type, currencyFormatter, { unitStyle: 'short' });
  const freightRateLine =
    displayedRates.rate !== null &&
    t(
      displayedRates.isCombined
        ? 'approvals.approve_charges.freight_and_material_rate'
        : 'approvals.approve_charges.freight_rate',
      {
        interpolation: { escapeValue: false },
        rate: formatShortRate(displayedRates.rate, rateType),
      },
    );
  const materialRateLine =
    displayedRates.separateMaterialRate !== null &&
    t('approvals.approve_charges.material_rate', {
      interpolation: { escapeValue: false },
      rate: formatShortRate(displayedRates.separateMaterialRate, materialRateType),
    });
  const taxLine =
    taxAmount > 0 &&
    t('approvals.approve_charges.tax_amount', {
      interpolation: { escapeValue: false },
      amount: currencyFormatter(taxAmount),
    });

  return (
    <Box
      data-test-id="charge-amount-cell"
      sx={[CHARGES_CELL_SX, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <OverflowAwareText
        sx={
          isRejected
            ? (theme) => ({
                color: theme.palette.text.disabled,
                fontWeight: 400,
                textDecoration: 'line-through',
              })
            : { fontWeight: 700 }
        }
      >
        {amount === null ? NO_VALUE_PLACEHOLDER : currencyFormatter(amount)}
      </OverflowAwareText>

      {isRejected && <RejectedChip reason={rejectedReason} />}

      {!isRejected && freightRateLine && (
        <OverflowAwareText data-test-id="charge-freight-rate">
          {freightRateLine}
        </OverflowAwareText>
      )}

      {!isRejected && materialRateLine && (
        <OverflowAwareText data-test-id="charge-material-rate">
          {materialRateLine}
        </OverflowAwareText>
      )}

      {!isRejected && taxLine && (
        <OverflowAwareText data-test-id="charge-tax-amount">{taxLine}</OverflowAwareText>
      )}

      {!isRejected && wasRate !== null && (
        <OverflowAwareText
          data-test-id="charge-was-rate"
          sx={(theme) => ({ color: theme.brandV2.colors.treadGray2 })}
        >
          {t('approvals.approve_charges.was', {
            amount: formatRateWithType(
              wasRate,
              rateType ?? materialRateType,
              currencyFormatter,
            ),
          })}
        </OverflowAwareText>
      )}

      {!isRejected && addOnsAmount > 0 && (
        <OverflowAwareText sx={{ textDecoration: 'underline dotted' }}>
          {t('approvals.approve_charges.add_ons_amount', {
            amount: currencyFormatter(addOnsAmount),
          })}
        </OverflowAwareText>
      )}
    </Box>
  );
}
